import { useEffect, useState } from 'react';

/**
 * Hook que retorna boolean indicando se a media query atual matchou.
 *
 * Útil pra renderizar componentes diferentes baseado no tamanho de tela
 * (em vez de só usar CSS responsivo).
 *
 * Exemplo:
 *   const isMobile = useMediaQuery('(max-width: 767px)');
 *   return isMobile ? <MobileNav /> : <DesktopNav />;
 *
 * Sincronizado com mudanças de viewport em tempo real (resize, rotação).
 * SSR-safe: retorna false até hidratar.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mq = window.matchMedia(query);
    setMatches(mq.matches);

    function handleChange(e: MediaQueryListEvent) {
      setMatches(e.matches);
    }

    // addEventListener é mais moderno; addListener fallback pra Safari antigo
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handleChange);
      return () => mq.removeEventListener('change', handleChange);
    } else {
      // Fallback pra Safari < 14: API legada deprecated mas ainda funcional
      mq.addListener(handleChange);
      return () => mq.removeListener(handleChange);
    }
  }, [query]);

  return matches;
}

/**
 * Helpers comuns alinhados com breakpoints do Tailwind.
 *
 *   const isMobile  = useIsMobile();    // < 768px
 *   const isTablet  = useIsTablet();    // 768px - 1023px
 *   const isDesktop = useIsDesktop();   // >= 1024px
 *   const isDark    = usePrefersDark(); // OS em modo escuro
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)');
}

export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
}

export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)');
}

export function usePrefersDark(): boolean {
  return useMediaQuery('(prefers-color-scheme: dark)');
}

export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/**
 * 🔴 O ponteiro consegue PAIRAR? (A3, 25/09/2026.)
 *
 * ⚠️ **Não é "é celular?".** Largura de tela responde sobre o TAMANHO; esta
 * pergunta é sobre a CAPACIDADE do ponteiro — e é ela que decide se um balão de
 * hover pode existir sem roubar o toque. **Tablet largo com dedo e notebook
 * estreito com mouse caem em lados opostos das duas perguntas.**
 *
 * ✅ **Onde isto importa:** o `<Tooltip>` canônico injeta um `onClick` com
 * `stopPropagation()` para funcionar no toque. **Num card ARRASTÁVEL do Kanban
 * isso engoliria o toque que ABRE o card** — o balão apareceria e o card não
 * abriria. ❌ Trocar um defeito por outro.
 */
export function usePodePairar(): boolean {
  return useMediaQuery('(hover: hover) and (pointer: fine)');
}
